import { useEffect, useState, memo, useCallback, useRef, useMemo } from "react";
import { IGif } from "@/common/services/types";
import { useMediaQuery } from "usehooks-ts";
import { WIDE_TABLET_WIDTH } from "@/common/utils/device";
import { useProgress } from "./hooks/useProgress";
import { usePlaybackControl } from "./hooks/usePlaybackControl";
import { useVisibilityChange } from "./hooks/useVideoEffects";
import { useScrollMore } from "./hooks/useScrollMore";
import { TapTracker } from "components/UI/TapTracker";
import { ProgressBar } from "components/UI/ProgressBar";
import PlayIcon from "@/common/images/icons/play.svg?react";
import { ReplayButton } from "../ReplayButton";
import { mediaHasAudio } from "./utils/mediaHasAudio";
import { apiMain } from "@/common/services";
import { createHlsUrl } from "@/common/utils/urls";
import "./Player.scss";
import { className } from "@/common/utils";
import { useAppStore, usePortableVideoStore } from "@/common/store";
import { useDismissScrollMore } from "./hooks/useDismissScrollMore";
import { useVisitorInfo } from "@/common/hooks";
import { useSfw } from "@/common/hooks/useSfw";
import { ScrollMorePopup } from "../ScrollMorePopup";

interface IPlayerProps {
  sound: boolean;
  fullscreen: boolean;
  handleLike: () => void;
  onSound: (sound: boolean, persistent?: boolean, showAnimation?: boolean) => void;
  isVisible: boolean;
  gif: IGif;
  isHLS: boolean;
  position: number;
  poster: string;
  currentUserName: string | undefined;
  sendGifView: (gif: IGif) => void;
  playCount: number;
  incrementPlayCount: () => void;
  onEnded?: () => void;
  onPlay?: () => void;
  onReplay?: () => void;
  loop: boolean;
}

export const Player = memo((props: IPlayerProps) => {
  const {
    sound,
    fullscreen,
    handleLike,
    onSound,
    isVisible,
    isHLS,
    position,
    poster,
    currentUserName,
    sendGifView,
    playCount,
    incrementPlayCount,
    onEnded,
    onPlay,
    onReplay,
    loop,
  } = props;

  const { sanitizeGif } = useSfw();
  const { gif, replacement } = sanitizeGif(props.gif);

  const startLoadTimeRef = useRef(0);
  const videoWrapRef = useRef(null);

  const [error, setError] = useState<string|null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [forceSd, setForceSd] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  const isMobile = useMediaQuery(`(max-width: ${WIDE_TABLET_WIDTH}px)`);
  const videoRef = usePortableVideoStore((state) => state.videoRef);
  const targetRef = usePortableVideoStore((state) => state.targetRef);
  const portVideo = usePortableVideoStore((state) => state.portVideo);
  const updateVideo = usePortableVideoStore((state) => state.updateVideo);

  const gifQuality = useAppStore((state) => state.gifQuality);

  const currentUser = useVisitorInfo();
  const { isDismissScrollMore, dismissScrollMore: onDismissScrollMore } = useDismissScrollMore(currentUser);

  const handleVideoEnded = useCallback(() => {
    setIsEnded(true);
    onEnded && onEnded();
  }, [onEnded]);

  const handleVideoPlay = useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    if (isEnded) {
      videoRef.current.currentTime = 0;
      onReplay && onReplay();
    }

    setIsEnded(false);
    onPlay && onPlay();
  }, [onPlay, onReplay, isEnded]);

  // play/pause controls
  const {
    pause,
    play,
    paused,
    setPaused,
    isTriedToPlay,
    startPlayback,
    handleEnded,
    handlePlay,
  } = usePlaybackControl({
    gif,
    videoRef,
    onSound,
    sendGifView,
    position,
    playCount,
    incrementPlayCount,
    onEnded: handleVideoEnded,
    onPlay: handleVideoPlay,
  });

  // progress bar controls
  const {
    progress,
    duration,
    firstTimeEnded,
    handleSeekBarChange,
    handleTimeUpdate,
    handleLoadedMetadata
  } = useProgress({
    videoRef,
    startPlayback,
    incrementPlayCount,
    paused,
  });

  // tab change actions
  useVisibilityChange({ videoRef, setPaused });

  // ScrollMorePopup controls
  const isShowScrollMore = useScrollMore({
    isVisible,
    firstTimeEnded,
    position,
    isDismissScrollMore,
    onDismissScrollMore
  });

  useEffect(() => {
    setIsActive(targetRef === videoWrapRef);
  }, [videoWrapRef, targetRef]);

  useEffect(() => {
    if (isVisible && !isActive) {
      portVideo(videoWrapRef);
    }
  }, [isVisible]);

  const videoUrl = useMemo(() => {
    // When the video is HLS format
    if (isHLS) {
      const quality = forceSd ? "sd" : fullscreen ? "hd" : gifQuality;
      return createHlsUrl(replacement?.id ?? gif.id, quality);
    }
    // Non-HLS format videos
    else if (forceSd) {
      return gif.urls.sd;
    }
    else if (fullscreen) {
      return gif.urls.hd || gif.urls.sd;
    }
    else {
      return gif.urls[gifQuality as "hd" | "sd"] || gif.urls.sd;
    }
  }, [fullscreen, gif, gifQuality, isHLS, forceSd]);

  useEffect(() => {
    if (!videoRef.current || !isActive) {
      return;
    }

    if (!isVisible && !videoRef.current.paused) {
      pause();
    }
  }, [isActive, isVisible]);

  useEffect(() => {
    if (isVisible && isLoaded) {
      play();
    }
  }, [isVisible, isLoaded]);

  useEffect(() => {
    if (!videoRef.current || !isActive || !isVisible || !loop) {
      return;
    }

    if (videoRef.current.paused) {
      play();
    }
  }, [loop]);

  const handleClick = useCallback(() => {
    if (!videoRef.current || error) {
      return;
    }

    if (!isActive) {
      portVideo(videoWrapRef);
      return;
    }

    videoRef.current.paused ? play() : pause();
  }, [isActive, videoWrapRef, error, play, pause, videoRef]);

  const handleError = useCallback(() => {
    const v = videoRef.current;

    if (v) {
      setError(v.networkState === v.NETWORK_NO_SOURCE ? "Error downloading the video file." : "Unknown error.");
      if (v.networkState === HTMLMediaElement.NETWORK_NO_SOURCE && !forceSd) {
        setForceSd(true);
        setError(null);
      }
    } else {
      console.error("Video playback failed, but the ref is not yet ready.");
    }
  }, [videoRef, forceSd, isHLS]);

  const handleLoadedData = useCallback(() => {
    setIsLoaded(true);

    if (currentUserName === gif.userName && gif.hasAudio !== mediaHasAudio(videoRef)) {
      apiMain.checkGifSound(gif.id);
    }
  }, [videoRef, gif]);

  const handleLoadStart = useCallback(() => {
    setIsLoaded(false);
    startLoadTimeRef.current = performance.now();
  }, []);

  const handleTapAction = useCallback((clicks: number) => {
    if (clicks === 1) {
      if (isMobile && fullscreen) {
        onSound(!gif.hasAudio ? false : !sound);
      } else {
        handleClick();
      }
    }

    if (clicks === 2) {
      handleLike();
    }
  }, [handleClick, handleLike, fullscreen, isMobile, onSound, sound, gif.hasAudio]);

  const handleHoldStart = useCallback(() => {
    if (fullscreen) {
      pause();
    }
  }, [pause, fullscreen]);

  const handleHoldEnd = useCallback(() => {
    if (fullscreen) {
      play();
    }
  }, [play, fullscreen]);

  const handleContextMenu = useCallback((
    event: React.MouseEvent<HTMLVideoElement, MouseEvent> |
    React.MouseEvent<SVGSVGElement, MouseEvent> |
    React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
  }, []);

  const contextMenuHandler = isMobile ? handleContextMenu : undefined;

  const handlePlayClick = useCallback(() => {
    if (fullscreen && isMobile) {
      handleClick();
    }
  }, [fullscreen, isMobile, handleClick]);

  const preload = useMemo(() => fullscreen ? "none" : "metadata", [fullscreen]);
  const videoAttributes = useMemo(() => {
    return {
      className: className({
        isError: !!error,
        isLoaded: isLoaded,
      }),
      controls: false,
      autoPlay: false,
      loop,
      playsInline: true,
      preload,
      onError: handleError,
      onLoadStart: handleLoadStart,
      onLoadedData: handleLoadedData,
      onLoadedMetadata: handleLoadedMetadata,
      onTimeUpdate: handleTimeUpdate,
      onPlay: handlePlay,
      onEnded: handleEnded,
      muted: !sound,
      disablePictureInPicture: true,
      src: videoUrl,
      onContextMenu: contextMenuHandler,
    };
  }, [
    sound,
    isLoaded,
    error,
    preload,
    handleError,
    handleLoadStart,
    handleLoadedData,
    handleLoadedMetadata,
    handleTimeUpdate,
    videoUrl,
    contextMenuHandler,
    loop,
    handlePlay,
    handleEnded,
  ]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    updateVideo(videoAttributes);
  }, [isActive, videoAttributes]);

  return (
    <>
      <TapTracker onTap={handleTapAction} onHold={handleHoldStart} onRelease={handleHoldEnd}>
        <div className="Player">
          <img className="Player-Poster" src={poster} alt={`Poster for ${gif.id}`}/>
          <div className="Player-Video" ref={videoWrapRef}></div>
          <div className="Player-OverLayer"></div>

          {isEnded && (
            <ReplayButton onClick={handlePlayClick} onContextMenu={contextMenuHandler} />
          )}

          {paused && isTriedToPlay && !error && !isEnded && (
            <PlayIcon
              onClick={handlePlayClick}
              className="Player-PlayButton"
              onContextMenu={contextMenuHandler}
            />
          )}

          {error && <p className="Player-ErrorMessage">{error}</p>}
        </div>
      </TapTracker>

      {
        !isEnded && (
          <div className="Player-ProgressBar">
            <ProgressBar
              max={duration}
              currentTime={progress}
              onChange={handleSeekBarChange}
              fullScreen={fullscreen}
            />
          </div>
        )
      }

      {/* video scroll more popup */}
      {
        isShowScrollMore && <ScrollMorePopup onCloseScrollMore={onDismissScrollMore} />
      }
    </>
  );
});
